import React, { useState, useEffect } from 'react';
import { productService, ProductError } from './productService';
import { GameCard, HomeResponse } from './types';
import AnnouncementCarousel from '../../components/AnnouncementCarousel';
import PlatformCarousel from '../../components/PlatformCarousel';
import ProductCarousel from '../../components/ProductCarousel';
import { Button } from '@/components/ui/button';

const ProductSkeleton = () => (
  <div className="overflow-hidden rounded-[28px] border border-foreground/10 bg-card animate-pulse">
    <div className="aspect-[4/5] w-full bg-foreground/5"></div>
    <div className="p-4">
      <div className="mb-2 h-5 rounded bg-foreground/5"></div>
      <div className="h-5 w-3/4 rounded bg-foreground/5"></div>
    </div>
  </div>
);

const HomeSection = ({
  title,
  link,
  products,
  hrefBase,
}: {
  title: string;
  link: string;
  products: GameCard[];
  hrefBase?: string;
}) => {
  if (products.length === 0) return null;

  return (
    <section className="mb-12">
      <div className="mb-5">
        <h2 className="text-2xl font-semibold text-foreground sm:text-3xl">{title}</h2>
      </div>

      <ProductCarousel products={products} hrefBase={hrefBase} viewAllHref={link} imageAndNameOnly />
    </section>
  );
};

const EMPTY_HOME: HomeResponse = {
  newReleases: [],
  salesLeaders: [],
  lowestPrices: [],
  topMetacritic: [],
  brandNew: [],
  preOrders: [],
  services: [],
  serviceGroups: [],
};

// Карусели сервисов на главной: порядок и заголовки задаются здесь, на фронте.
// category — значение ServiceCategory из бэкенда (не менять), title — как хочешь.
const SERVICE_CATEGORY_SECTIONS: { category: string; title: string }[] = [
  { category: 'GameService', title: 'Игровые сервисы' },
  { category: 'AI', title: 'AI' },
  { category: 'Entertainment', title: 'Развлечения' },
  { category: 'Work', title: 'Для работы' },
  { category: 'Other', title: 'Другие сервисы' },
];

const ProductList = () => {
  const [home, setHome] = useState<HomeResponse>(EMPTY_HOME);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      setHome(await productService.fetchHome());
    } catch (err) {
      const productError = err as ProductError;
      setError(productError.message || 'Не удалось загрузить данные');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background px-4 py-6">
        <div className="mx-auto max-w-[90rem]">
          <div className="mb-8 h-40 rounded-[32px] border border-foreground/10 bg-foreground/5" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[...Array(6)].map((_, i) => (
              <ProductSkeleton key={i} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background py-4 px-4">
        <div className="mx-auto max-w-[90rem]">
          <div className="text-center py-12">
            <p className="text-gray-400 mb-6">{error}</p>
            <Button
              onClick={load}
              variant="destructive"
              className="inline-flex items-center px-6 py-3 rounded-md text-base font-medium"
            >
              Повторить
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-x-hidden bg-background px-4 py-6 sm:py-8">
      <div className="mx-auto max-w-[90rem]">

        <AnnouncementCarousel />

        <PlatformCarousel />

        {/* Отдельная карусель на каждую категорию онлайн-сервисов.
            Порядок — как в SERVICE_CATEGORY_SECTIONS; пустые группы скрываются сами. */}
        {SERVICE_CATEGORY_SECTIONS.map(section => {
          const group = home.serviceGroups.find(g => g.category === section.category);
          if (!group) return null;
          return (
            <HomeSection
              key={section.category}
              title={section.title}
              link={`/services/${section.category}`}
              products={group.services}
              hrefBase="/service"
            />
          );
        })}
      </div>
    </div>
  );
};

export default ProductList;
